import { useEffect, useRef, useState } from 'react'
import { Play } from 'lucide-react'
import { ThumbFallback } from './CreatorVideoThumbs'
import { hasStorageVideo } from '../hooks/useVideoWatchModal'
import { PINK } from '../lib/theme'


/**
 * Roster-style 9:16 video tile for a submitted video: poster + play
 * affordance + legibility gradient + pink hover ring, with the branded
 * ThumbFallback when the TikTok thumbnail URL has expired (TikTok CDN
 * links rot after hours/days — mirroring to storage is the backend fix).
 *
 * Clicking opens the "watch" popup (same in-app modal as Find Creators) when
 * `onWatchClick` is passed — the caller resolves a playable URL on demand via
 * the video-download endpoint's mirror-to-R2 flow, since submitted videos
 * have no mirrored MP4 up front like creator top_video_urls do. Falls back to
 * a plain TikTok link-out when no handler is given.
 */
export function SubmissionThumb({ thumbnailUrl, tiktokUrl, videoUrl, width = 48, fluid = false, onWatchClick }: {
  thumbnailUrl: string | null
  tiktokUrl: string | null
  /** Durable mirrored MP4 (video_storage_url). When set, hovering the tile
   *  auto-plays a muted looping preview, TikTok-style. Videos that haven't
   *  been mirrored yet can't preview (the TikTok URL is a page, not a file). */
  videoUrl?: string | null
  width?: number
  /** Fill the parent's width and keep 9:16 via aspect-ratio (`width` is
   *  ignored). For fluid-grid tiles like the Home recent-videos strip. */
  fluid?: boolean
  onWatchClick?: () => void
}) {
  const [thumbError, setThumbError] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)

  // Short delay before mounting the <video> so sweeping the cursor across a
  // grid of tiles doesn't kick off a network fetch per tile passed over.
  const hoverTimer = useRef<number | null>(null)
  const startPreview = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current)
    hoverTimer.current = window.setTimeout(() => setPreviewing(true), 120)
  }
  const stopPreview = () => {
    if (hoverTimer.current) { window.clearTimeout(hoverTimer.current); hoverTimer.current = null }
    setPreviewing(false)
  }
  useEffect(() => () => { if (hoverTimer.current) window.clearTimeout(hoverTimer.current) }, [])

  const showPreview = previewing && !previewFailed && hasStorageVideo(videoUrl)

  const baseShadow = 'inset 0 0 0 1px rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.30)'
  const tileStyle: React.CSSProperties = {
    position: 'relative',
    ...(fluid
      ? { width: '100%', aspectRatio: '9 / 16' }
      : { width, height: Math.round(width * (16 / 9)) }),
    flexShrink: 0,
    display: 'block',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#0a0a0e',
    boxShadow: baseShadow,
    transition: 'box-shadow 140ms ease, transform 140ms ease',
  }

  const inner = (
    <>
      {thumbnailUrl && !thumbError ? (
        <img
          src={thumbnailUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setThumbError(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ThumbFallback />
      )}
      {/* Hover preview — muted looping inline playback over the poster. The
          poster stays visible underneath until the first frame decodes, so
          there's no flash while the file loads. */}
      {showPreview && (
        <video
          src={videoUrl as string}
          autoPlay muted loop playsInline
          onError={() => setPreviewFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {/* Play affordance + legibility gradient — the Find Creators treatment.
          Hidden while the hover preview is playing. */}
      {!showPreview && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.20)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.85)',
          }}>
            <Play size={9} fill="currentColor" strokeWidth={0} />
          </div>
        </div>
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.5) 100%)', pointerEvents: 'none' }} />
    </>
  )

  if (!tiktokUrl) {
    return <div style={tileStyle} onMouseEnter={startPreview} onMouseLeave={stopPreview}>{inner}</div>
  }

  const hoverHandlers = {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.style.boxShadow = `0 0 0 2px ${PINK}, 0 4px 14px rgba(0,0,0,0.4)`
      startPreview()
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.style.boxShadow = baseShadow
      stopPreview()
    },
  }

  if (onWatchClick) {
    return (
      <button
        type="button"
        onClick={onWatchClick}
        title="Watch"
        {...hoverHandlers}
        style={{ ...tileStyle, border: 0, padding: 0, cursor: 'pointer' }}
      >
        {inner}
      </button>
    )
  }

  return (
    <a
      href={tiktokUrl}
      target="_blank" rel="noopener noreferrer"
      title="Watch on TikTok"
      {...hoverHandlers}
      style={tileStyle}
    >
      {inner}
    </a>
  )
}
